import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { renderMarkdown } from '@dia/content';
import { useT } from '@dia/i18n';
import { Button } from '@dia/ui';
import { supabase } from '../supabase';
import { RequireAuth } from './lib/identity';
import { Icon } from './lib/icons';
import { loadAssets, useLoad } from './lib/data';
import { docForAsset, parseSystemsBody } from './lib/systems';
import { opsContent } from './lib/content';
import { Card, Notice, Page } from './lib/ui';

/**
 * One page per asset: a video walkthrough, the numbered do-this list, and the
 * "if it fails, do this first, then call" line.
 *
 * Nothing here is invented. Where nobody has recorded the procedure yet (the
 * Director of Operations records them before the end of the month), the page
 * says so in plain words and shows only the facts the assets table holds.
 */
export function SistemasAsset() {
  return <RequireAuth>{() => <AssetBody />}</RequireAuth>;
}

function AssetBody() {
  const { slug = '' } = useParams();
  const { t, lang } = useT();
  const assets = useLoad(loadAssets, 'assets');
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoFailed, setVideoFailed] = useState(false);

  const asset = assets.data?.find((a) => a.slug === slug) ?? null;
  const docs = opsContent.section('sistemas');
  const doc = docForAsset(docs, slug) ?? docs.find((d) => d.slug === slug);
  const page = doc ? parseSystemsBody(doc.body)[lang] : null;
  const title = asset ? (lang === 'es' ? asset.name_es : asset.name_en) : (doc?.title[lang] ?? '');

  if (!asset && !doc) {
    return (
      <Page title={t('ops.nav.systems')} back="/sistemas">
        {assets.loading ? <p>{t('loading')}</p> : <Notice tone="warn">{t('ops.systems.not_found')}</Notice>}
      </Page>
    );
  }

  const videos = Array.isArray(asset?.video_paths) ? (asset.video_paths as unknown[]).filter((v): v is string => typeof v === 'string') : [];
  const notes = asset ? (lang === 'es' ? asset.notes_es : asset.notes_en) : null;

  const playVideo = async () => {
    const first = videos[0];
    if (!first) return;
    const { data, error } = await supabase.storage.from('systems-media').createSignedUrl(first, 3600);
    if (error || !data) setVideoFailed(true);
    else setVideoUrl(data.signedUrl);
  };

  return (
    <Page title={title} back="/sistemas">
      <div className="flex flex-col gap-5">
        {/* Video walkthrough. Streams on demand, never precached. */}
        <Card>
          {videos.length > 0 ? (
            videoUrl ? (
              <video controls preload="none" src={videoUrl} className="w-full rounded" />
            ) : (
              <div className="flex flex-col gap-2">
                <Button size="lg" block onClick={() => void playVideo()}>
                  <Icon name="play" /> {t('ops.systems.watch_video')}
                </Button>
                {videoFailed && <Notice tone="warn">{t('ops.systems.video_placeholder')}</Notice>}
              </div>
            )
          ) : (
            <p className="m-0 flex items-center gap-2 text-inkMuted">
              <Icon name="play" /> {t('ops.systems.video_placeholder')}
            </p>
          )}
        </Card>

        {page?.recorded ? (
          <>
            {page.intro && <p>{page.intro}</p>}
            <Card>
              <h2 className="mb-2 text-[1.15em] font-semibold">{t('ops.systems.steps')}</h2>
              <ol className="m-0 flex list-decimal flex-col gap-3 pl-6">
                {page.steps.map((step) => (
                  <li key={step} className="text-[1.05em]">{step}</li>
                ))}
              </ol>
            </Card>
            {page.ifItFails && (
              <div className="rounded border-2 border-danger bg-surface p-4">
                <h2 className="mb-2 text-[1.15em] font-semibold text-danger">{t('ops.systems.if_it_fails')}</h2>
                <div
                  className="[&_p]:m-0"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(page.ifItFails) }}
                />
              </div>
            )}
          </>
        ) : (
          <Card>
            <h2 className="mb-2 text-[1.15em] font-semibold">{t('ops.systems.not_recorded')}</h2>
            <p className="m-0">{t('ops.systems.not_recorded_help')}</p>
          </Card>
        )}

        {asset && (
          <Card>
            <h2 className="mb-2 text-[1.15em] font-semibold">{t('ops.systems.data_title')}</h2>
            <dl className="m-0 flex flex-col gap-2">
              {asset.location && (
                <div>
                  <dt className="font-medium">{t('ops.systems.where')}</dt>
                  <dd className="m-0 text-inkMuted">{asset.location}</dd>
                </div>
              )}
              {(asset.make || asset.model) && (
                <div>
                  <dt className="font-medium">{t('ops.systems.make')}</dt>
                  <dd className="m-0 text-inkMuted">{[asset.make, asset.model].filter(Boolean).join(' ')}</dd>
                </div>
              )}
              {notes && <dd className="m-0">{notes}</dd>}
            </dl>
          </Card>
        )}

        <Link to={`/mantenimiento?asset=${encodeURIComponent(slug)}`} className="no-underline">
          <Button variant="secondary" block>
            <Icon name="wrench" /> {t('ops.systems.report_this')}
          </Button>
        </Link>
      </div>
    </Page>
  );
}
